/**
 * Branded numeric IDs. The backend uses plain `int` for every entity key, which makes
 * it easy to pass an audit id where a model id belongs. Branding keeps them distinct at
 * compile time while staying a plain number at runtime.
 */
declare const brand: unique symbol;
type Brand<T, TBrand> = T & { readonly [brand]: TBrand };

export type UserId = Brand<string, 'UserId'>;
/**
 * A user as the Core service numbers them, which is not how the identity provider does.
 *
 * `UserId` is the IdentityServer subject — a GUID string that identifies who is signed in.
 * Core's own `AuthorId`, `ExecutedBy` and `CreatedBy` columns are `int` keys into its user
 * table, a different id space entirely. They are kept apart so that neither can be passed
 * where the other belongs, and so nobody tries to compare the two.
 */
export type CoreUserId = Brand<number, 'CoreUserId'>;
export type FamilyId = Brand<number, 'FamilyId'>;
/**
 * A stored Revit document, which is what the legacy `/projects` pages call a project.
 *
 * On those routes there is no separate project key to brand. `RevitFIleDocument.Id` is what
 * `SnapshotMetaData.RevitModelId`, `AuditInstance.RevitFileDocumentId` and
 * `RevitElement.ProjectId` all point at, so one brand covers the lot; a second would only
 * create casts between two names for the same number.
 */
export type ModelId = Brand<number, 'ModelId'>;
export type SnapshotId = Brand<number, 'SnapshotId'>;
export type DiffId = Brand<number, 'DiffId'>;
export type AuditId = Brand<number, 'AuditId'>;
/** `AuditInstance.Id` — one execution of an audit, which the client calls a run. */
export type AuditRunId = Brand<number, 'AuditRunId'>;
export type TagId = Brand<number, 'TagId'>;

/**
 * A project in the Core workspace (`/api/v1/projects`): the container that owns members and
 * documents and links to several Revit models. A GUID, and never a `ModelId`.
 */
export type ProjectId = Brand<string, 'ProjectId'>;
export type TenantId = Brand<string, 'TenantId'>;
export type ProjectMemberId = Brand<string, 'ProjectMemberId'>;
/** A project document (`/api/v1/projects/{id}/documents`); one identity across all its views. */
export type DocumentId = Brand<string, 'DocumentId'>;
export type RevisionId = Brand<string, 'RevisionId'>;
/** An upload session (`/api/v1/projects/{id}/uploads`) and the stored file it produces. */
export type UploadId = Brand<string, 'UploadId'>;
export type AssetId = Brand<string, 'AssetId'>;

export const toFamilyId = (value: number): FamilyId => value as FamilyId;
export const toModelId = (value: number): ModelId => value as ModelId;
export const toSnapshotId = (value: number): SnapshotId => value as SnapshotId;
export const toDiffId = (value: number): DiffId => value as DiffId;
export const toAuditId = (value: number): AuditId => value as AuditId;
export const toAuditRunId = (value: number): AuditRunId => value as AuditRunId;
export const toTagId = (value: number): TagId => value as TagId;
export const toUserId = (value: string): UserId => value as UserId;
export const toCoreUserId = (value: number): CoreUserId => value as CoreUserId;
export const toProjectId = (value: string): ProjectId => value as ProjectId;
export const toTenantId = (value: string): TenantId => value as TenantId;
export const toProjectMemberId = (value: string): ProjectMemberId => value as ProjectMemberId;
export const toDocumentId = (value: string): DocumentId => value as DocumentId;
export const toRevisionId = (value: string): RevisionId => value as RevisionId;
export const toUploadId = (value: string): UploadId => value as UploadId;
export const toAssetId = (value: string): AssetId => value as AssetId;

/** Zero-based pagination matching the Core service's `currentPage` / `pageSize` contract. */
export interface PageRequest {
  readonly page: number;
  readonly pageSize: number;
}

export interface Paginated<T> {
  readonly items: readonly T[];
  readonly totalCount: number;
  readonly page: number;
  readonly pageSize: number;
}

export const DEFAULT_PAGE_SIZE = 20;

export type SortDirection = 'asc' | 'desc';

export interface SortSpec<TField extends string> {
  readonly field: TField;
  readonly direction: SortDirection;
}

/** Loading state for views that must distinguish "not asked yet" from "loaded empty". */
export type AsyncStatus = 'idle' | 'loading' | 'success' | 'error';
